import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, ArrowLeft, Loader2, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import { useData } from "../context/DataContext";
import { toast } from "sonner";
import api from "../utils/api";
import AccessRestricted from "../components/AccessRestricted";

export default function PeopleSettingsPage() {
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);
  const { employmentEntities, fetchEmploymentEntities } = useData();
  const [newOrgName, setNewOrgName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    if (employmentEntities.length === 0) {
      fetchEmploymentEntities();
    }
  }, []);

  if (!ability.can(ACTIONS.READ, RESOURCES.PERSON_SETTINGS) && !ability.can(ACTIONS.READ, RESOURCES.PERSON)) {
    return <AccessRestricted />;
  }

  const handleAdd = async () => {
    if (!newOrgName.trim()) {
      toast.error("Employment Entity name is required");
      return;
    }
    try {
      setSaving(true);
      const response = await api.post("/api/employment-entities", { name: newOrgName.trim() });
      if (response && response.success) {
        setNewOrgName("");
        toast.success("Employment Entity added successfully");
        fetchEmploymentEntities();
      }
    } catch (error) {
      console.error("Error adding employment entity:", error);
      toast.error("Failed to add employment entity");
    } finally {
      setSaving(false);
    }
  };

  const promptDelete = (entity: { id: number; name: string }) => {
    setEntityToDelete(entity);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!entityToDelete) return;
    try {
      setSaving(true);
      await api.delete(`/api/employment-entities/${entityToDelete.id}`);
      toast.success("Employment Entity deleted successfully");
      setDeleteDialogOpen(false);
      setEntityToDelete(null);
      fetchEmploymentEntities();
    } catch (error: any) {
      console.error("Error deleting employment entity:", error);
      toast.error(error.response?.data?.message || "Failed to delete employment entity");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">People Settings</h1>
          <p className="text-sm text-muted-foreground">
            Manage employment entities available for selection
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-6">
        <div className="space-y-8">
          <div className="flex gap-3 items-end">
            <div className="space-y-2 flex-1 max-w-sm">
              <Label htmlFor="orgName" className="font-bold text-gray-700">
                Add New Employment Entity
              </Label>
              <Input
                id="orgName"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="Enter employment entity name"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAdd();
                }}
              />
            </div>
            <Button
              onClick={handleAdd}
              disabled={saving || !newOrgName.trim()}
              className="bg-slate-950 hover:bg-slate-900 text-white"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
              Add Employment Entity
            </Button>
          </div>

          <div className="space-y-4">
            <h4 className="font-bold text-gray-900 border-b pb-2">Registered Employment Entities</h4>
            {employmentEntities.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed">
                <Building2 className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No employment entities added yet.</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden bg-white">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employment Entity Name
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {employmentEntities.map((entity) => (
                      <tr key={entity.id} className="hover:bg-slate-50 transition-colors group">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {entity.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => promptDelete(entity)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              These employment entities will be available to select when adding or editing people.
            </p>
          </div>
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={20} />
              Delete Employment Entity
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold text-gray-900">"{entityToDelete?.name}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving} onClick={() => setEntityToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 size={16} className="mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
